import { Shell } from '@/components/Shell';
import { AddonForm } from '@/components/settings/AddonForm';
import {
  BankAccountsSetting,
  CancellationPolicySetting,
  CompanyNameSetting,
  CookiesSetting,
  DepositPctSetting,
  EmailSetting,
  FaqSetting,
  HoldMinutesSetting,
  HomePromoSetting,
  OverdueGraceSetting,
  PhoneSetting,
  PrivacySetting,
  TermsSetting,
  YoungDriverRuleSetting,
} from '@/components/settings/SettingForms';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { type AddonRow, adminApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/settings')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: SettingsPage,
});

type Tab = 'company' | 'home' | 'payments' | 'policies' | 'faq' | 'operational' | 'addons';

const TABS: { key: Tab; label: string }[] = [
  { key: 'company', label: 'EMPRESA' },
  { key: 'home', label: 'PÁGINA INICIAL' },
  { key: 'payments', label: 'PAGAMENTOS' },
  { key: 'policies', label: 'POLÍTICAS' },
  { key: 'faq', label: 'FAQ' },
  { key: 'operational', label: 'OPERACIONAL' },
  { key: 'addons', label: 'EXTRAS' },
];

function SettingsPage() {
  const [tab, setTab] = useState<Tab>('company');

  return (
    <Shell page="DEFINIÇÕES" sub="ADMIN · CONFIGURAÇÃO DO SISTEMA">
      {/* Tab strip */}
      <nav
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 20,
          borderBottom: '1px solid var(--lv-line)',
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              background: tab === t.key ? 'var(--lv-brand)' : 'transparent',
              color: tab === t.key ? '#fff' : 'var(--lv-ink)',
              border: 0,
              padding: '10px 20px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'background 0.1s, color 0.1s',
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'company' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <CompanyNameSetting />
          <EmailSetting />
          <PhoneSetting />
        </div>
      )}
      {tab === 'home' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <HomePromoSetting />
        </div>
      )}
      {tab === 'payments' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <DepositPctSetting />
          <BankAccountsSetting />
        </div>
      )}
      {tab === 'policies' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <CancellationPolicySetting />
          <TermsSetting />
          <PrivacySetting />
          <CookiesSetting />
        </div>
      )}
      {tab === 'faq' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <FaqSetting />
        </div>
      )}
      {tab === 'operational' && (
        <div style={{ display: 'grid', gap: 16 }}>
          <YoungDriverRuleSetting />
          <HoldMinutesSetting />
          <OverdueGraceSetting />
        </div>
      )}
      {tab === 'addons' && <AddonsCRUD />}
    </Shell>
  );
}

function AddonsCRUD() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'addons'], queryFn: () => adminApi.addons() });
  const [editing, setEditing] = useState<AddonRow | 'new' | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteAddon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'addons'] }),
  });

  return (
    <section style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 16,
        }}
      >
        <div>
          <div className="label" style={{ color: 'var(--lv-brand)' }}>
            CATÁLOGO DE EXTRAS
          </div>
          <div className="h-rule" style={{ marginTop: 8, marginBottom: 8 }} />
          <p style={{ fontSize: 12, color: 'var(--lv-gray-500)', marginTop: 4 }}>
            Cadeira de criança, seguro premium, motorista privado, etc.
          </p>
        </div>
        <button type="button" onClick={() => setEditing('new')} style={btnPrimary}>
          + Novo extra
        </button>
      </div>

      <div style={{ border: '1px solid var(--lv-line)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--lv-night)' }}>
              {['CHAVE', 'NOME (PT)', 'EUR', 'CVE', 'ACTIVO', ''].map((h) => (
                <th
                  key={h}
                  className="label"
                  style={{
                    color: 'var(--lv-brand-glow)',
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  A carregar…
                </td>
              </tr>
            )}
            {data?.items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  Sem extras. Adiciona o primeiro acima.
                </td>
              </tr>
            )}
            {data?.items.map((a) => (
              <tr key={a.id} style={{ borderTop: '1px solid var(--lv-line)' }}>
                <td
                  style={{
                    padding: '12px 16px',
                    fontFamily: 'var(--lv-font-mono)',
                    fontSize: 12,
                    color: 'var(--lv-gray-500)',
                  }}
                >
                  {a.key}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <button
                    type="button"
                    onClick={() => setEditing(a)}
                    style={{
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      color: 'var(--lv-brand)',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    {a.name.pt || '—'}
                  </button>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span className="display" style={{ fontSize: 18 }}>
                    {a.eurDefault.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--lv-gray-500)', marginLeft: 3 }}>
                    EUR
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--lv-gray-500)' }}>
                  {a.cveDefault.toFixed(0)} CVE
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      color: a.active ? 'var(--lv-ok)' : 'var(--lv-warn)',
                    }}
                  >
                    ● {a.active ? 'SIM' : 'NÃO'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                  <ConfirmButton
                    label="✕"
                    question={`Eliminar extra "${a.key}"?`}
                    onConfirm={() => remove.mutate(a.id)}
                    style={btnSmallDanger}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <AddonForm
          addon={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'addons'] });
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '10px 20px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  flexShrink: 0,
};
const btnSmallDanger: React.CSSProperties = {
  background: '#fff',
  color: 'var(--lv-err)',
  border: '1px solid var(--lv-err)',
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
